/**
 * Alpine.js Global Store for reactive state management
 * Syncs with UserRepository to provide reactive UI updates
 */

import type { VoiceType } from '../types/user.types';

// Alpine store type definition
export interface AlpineUserStore {
  email: string;
  voice: VoiceType;
  feeling: string;
  goal: string;
  superpower: string;
  motivation: string;
  genres: string[];
  venue: string;
  practiceFrequency: string;
  abTestVariant: string;
  // Voice detection results
  detectedVoiceType: string;
  detectedVoiceTypeKey: string;
  detectedPitch: string;
  voiceDetectionSkipped: boolean;

  init(): void;
  setAnswer(key: string, value: string | string[] | boolean): void;
  setEmail(email: string): void;
  isMale(): boolean;
  isFemale(): boolean;
  hasVoiceSelection(): boolean;
  getTestimonialName(): string;
  getAvatarImage(): string;
}

// Bridge functions for Alpine.js integration
export function syncAlpineStore(key: string, value: string | string[] | boolean): void {
  if (typeof (window as any).Alpine !== 'undefined') {
    const Alpine = (window as any).Alpine;
    if (Alpine && Alpine.store('user')) {
      Alpine.store('user').setAnswer(key, value);
    }
  }
}

export function syncAlpineEmail(email: string): void {
  if (typeof (window as any).Alpine !== 'undefined') {
    const Alpine = (window as any).Alpine;
    if (Alpine && Alpine.store('user')) {
      Alpine.store('user').setEmail(email);
    }
  }
}

/**
 * Initialize Alpine store
 * This is called by Alpine.js when it's ready
 */
export function initAlpineStore(): void {
  console.log('[Alpine Store] Script loaded, waiting for Alpine...');

  // Wait for Alpine to be ready
  document.addEventListener('alpine:init', () => {
    console.log('[Alpine Store] Initializing Alpine store...');

    const Alpine = (window as any).Alpine;
    if (!Alpine) {
      console.error('[Alpine Store] Alpine.js not available');
      return;
    }

    // Create a global store for user data
    Alpine.store('user', {
      // User preferences and answers
      email: '',
      voice: '' as VoiceType,
      feeling: '',
      goal: '',
      superpower: '',
      motivation: '',
      genres: [] as string[],
      venue: '',
      practiceFrequency: '',
      abTestVariant: '',
      // Voice detection results
      detectedVoiceType: '',
      detectedVoiceTypeKey: '',
      detectedPitch: '',
      voiceDetectionSkipped: false,

      // Initialize from UserRepository if available
      init(): void {
        // Sync with existing UserRepository data if available
        const UserRepository = (window as any).UserRepository;
        if (typeof UserRepository !== 'undefined') {
          const existingData = UserRepository.getAllData();
          if (existingData && existingData.answers) {
            // Map answers to store properties
            this.voice = (existingData.answers.voice || '') as VoiceType;
            this.feeling = existingData.answers.feel || '';
            this.goal = existingData.answers.goal || '';
            this.superpower = existingData.answers.superpower || '';
            this.motivation = existingData.answers.motivation || '';
            this.genres = existingData.answers.music || [];
            this.venue = existingData.answers.venue || '';
            this.practiceFrequency = existingData.answers.practice || '';
            // Voice detection results
            this.detectedVoiceType = existingData.answers.detectedVoiceType || '';
            this.detectedVoiceTypeKey = existingData.answers.detectedVoiceTypeKey || '';
            this.detectedPitch = existingData.answers.detectedPitch || '';
            this.voiceDetectionSkipped = existingData.answers.voiceDetectionSkipped || false;
          }
          if (existingData && existingData.email) {
            this.email = existingData.email;
          }
          this.abTestVariant = UserRepository.getABTestVariant();
          console.log(
            '[Alpine Store] abTestVariant set to:',
            this.abTestVariant,
            typeof this.abTestVariant
          );
        } else {
          console.warn(
            '[Alpine Store] UserRepository not available during init - data sync disabled'
          );
        }
      },

      // Update a specific answer
      setAnswer(key: string, value: string | string[] | boolean): void {
        switch (key) {
          case 'voice':
            this.voice = value as VoiceType;
            break;
          case 'feel':
          case 'feeling':
            this.feeling = value as string;
            break;
          case 'goal':
            this.goal = value as string;
            break;
          case 'superpower':
            this.superpower = value as string;
            break;
          case 'motivation':
            this.motivation = value as string;
            break;
          case 'music':
          case 'genres':
            this.genres = Array.isArray(value) ? value : [value];
            break;
          case 'venue':
            this.venue = value as string;
            break;
          case 'practice':
          case 'practiceFrequency':
            this.practiceFrequency = value as string;
            break;
          // Voice detection results
          case 'detectedVoiceType':
            this.detectedVoiceType = value as string;
            break;
          case 'detectedVoiceTypeKey':
            this.detectedVoiceTypeKey = value as string;
            break;
          case 'detectedPitch':
            this.detectedPitch = value as string;
            break;
          case 'voiceDetectionSkipped':
            this.voiceDetectionSkipped = value === true || value === 'true';
            break;
        }

        // Also update UserRepository to maintain compatibility
        const UserRepository = (window as any).UserRepository;
        if (typeof UserRepository !== 'undefined') {
          UserRepository.setAnswer(key, value);
        } else {
          console.warn(
            '[Alpine Store] UserRepository not available - cannot sync answer:',
            key,
            value
          );
        }
      },

      // Set email
      setEmail(email: string): void {
        this.email = email;
        const UserRepository = (window as any).UserRepository;
        if (typeof UserRepository !== 'undefined') {
          UserRepository.setEmail(email);
        } else {
          console.warn(
            '[Alpine Store] UserRepository not available - cannot sync email:',
            email
          );
        }
      },

      // Helper getters for common checks
      isMale(): boolean {
        return this.voice === 'male';
      },

      isFemale(): boolean {
        return this.voice === 'female';
      },

      hasVoiceSelection(): boolean {
        return this.voice !== '';
      },

      // Get display name based on gender for testimonials
      getTestimonialName(): string {
        return this.isMale() ? 'Michael R.' : 'Sarah M.';
      },

      // Get avatar image based on gender (returns AVIF, fallback handled in HTML)
      getAvatarImage(): string {
        return this.isMale()
          ? 'assets/images/avatar-male-adult.avif'
          : 'assets/images/avatar-female-adult.avif';
      },
    });

    console.log('[Alpine Store] Store initialized with data:', Alpine.store('user'));
  });
}

// Make bridge functions globally available
if (typeof window !== 'undefined') {
  (window as any).syncAlpineStore = syncAlpineStore;
  (window as any).syncAlpineEmail = syncAlpineEmail;
}

export default {
  initAlpineStore,
  syncAlpineStore,
  syncAlpineEmail,
};
